import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Card,
  Collapse,
  Group,
  Loader,
  ScrollArea,
  Text,
  UnstyledButton,
} from '@mantine/core'
import {
  IconChevronDown,
  IconChevronRight,
  IconColumns,
  IconDatabase,
  IconFolder,
  IconTable,
} from '@tabler/icons-react'
import {
  getTable,
  listCatalogs,
  listSchemas,
  listTables,
} from '../../api/databricks'

type ExplorerNodeType = 'catalog' | 'schema' | 'table' | 'column'

interface ExplorerNode {
  id: string
  name: string
  type: ExplorerNodeType
  fullName: string
  children?: ExplorerNode[]
  loading?: boolean
  expanded?: boolean
}

const SYSTEM_CATALOGS = new Set(['system', 'samples'])

function getIcon(type: ExplorerNodeType) {
  if (type === 'catalog') return <IconDatabase size={16} />
  if (type === 'schema') return <IconFolder size={16} />
  if (type === 'table') return <IconTable size={16} />
  return <IconColumns size={16} />
}

interface ExplorerItemProps {
  node: ExplorerNode
  level: number
  onToggle: (node: ExplorerNode) => void
  onInsert: (name: string) => void
}

function ExplorerItem({ node, level, onToggle, onInsert }: ExplorerItemProps) {
  const hasChildren = node.type !== 'column'
  const Chevron = node.expanded ? IconChevronDown : IconChevronRight

  return (
    <Box>
      <UnstyledButton
        w="100%"
        onClick={() => {
          if (hasChildren) onToggle(node)
          if (node.type === 'table') onInsert(node.fullName)
        }}
        style={{ paddingLeft: level * 16 + 4, paddingTop: 2, paddingBottom: 2 }}
      >
        <Group gap={4} wrap="nowrap">
          {hasChildren ? <Chevron size={14} /> : <Box w={14} />}
          {getIcon(node.type)}
          <Text size="sm" lineClamp={1} style={{ flex: 1 }}>
            {node.name}
          </Text>
          {node.loading && <Loader size={14} />}
        </Group>
      </UnstyledButton>
      <Collapse in={node.expanded ?? false}>
        {node.children?.map((child) => (
          <ExplorerItem
            key={child.id}
            node={child}
            level={level + 1}
            onToggle={onToggle}
            onInsert={onInsert}
          />
        ))}
      </Collapse>
    </Box>
  )
}

function updateNodeTree(
  nodes: ExplorerNode[],
  id: string,
  patch: Partial<ExplorerNode>
): ExplorerNode[] {
  return nodes.map((node) => {
    if (node.id === id) return { ...node, ...patch }
    if (node.children) {
      return {
        ...node,
        children: updateNodeTree(node.children, id, patch),
      }
    }
    return node
  })
}

export function SchemaExplorer({
  onInsert,
}: {
  onInsert: (name: string) => void
}) {
  const [nodes, setNodes] = useState<ExplorerNode[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    listCatalogs()
      .then((catalogs) =>
        catalogs
          .filter((catalog) => !SYSTEM_CATALOGS.has(catalog.name.toLowerCase()))
          .map((catalog) => ({
            id: `catalog-${catalog.name}`,
            name: catalog.name,
            type: 'catalog' as const,
            fullName: catalog.name,
          }))
      )
      .then(setNodes)
      .finally(() => setLoading(false))
  }, [])

  const handleToggle = useCallback(async (node: ExplorerNode) => {
    if (node.type === 'column') return
    if (node.expanded) {
      setNodes((prev) => updateNodeTree(prev, node.id, { expanded: false }))
      return
    }

    if (node.children) {
      setNodes((prev) => updateNodeTree(prev, node.id, { expanded: true }))
      return
    }

    setNodes((prev) => updateNodeTree(prev, node.id, { loading: true }))
    let children: ExplorerNode[] | undefined
    try {
      if (node.type === 'catalog') {
        const schemas = await listSchemas(node.fullName)
        children = schemas.map((schema) => ({
          id: `schema-${node.fullName}.${schema.name}`,
          name: schema.name,
          type: 'schema' as const,
          fullName: `${node.fullName}.${schema.name}`,
        }))
      } else if (node.type === 'schema') {
        const [catalogName, schemaName] = node.fullName.split('.')
        const tables = await listTables(catalogName, schemaName)
        children = tables.map((table) => ({
          id: `table-${table.full_name}`,
          name: table.name,
          type: 'table' as const,
          fullName: table.full_name,
        }))
      } else if (node.type === 'table') {
        const table = await getTable(node.fullName)
        children = (table.columns ?? []).map((column, index) => ({
          id: `column-${node.fullName}.${column.name}-${index}`,
          name: `${column.name} (${column.type_text ?? column.type_name ?? '?'})`,
          type: 'column' as const,
          fullName: column.name,
        }))
      }
    } catch {
      // leave children empty on error
    }

    setNodes((prev) =>
      updateNodeTree(prev, node.id, {
        children,
        loading: false,
        expanded: true,
      })
    )
  }, [])

  return (
    <Card withBorder h="100%">
      <Text fw={700} mb="xs">
        Schema explorer
      </Text>
      <ScrollArea h={500}>
        {loading ? (
          <Loader size="sm" />
        ) : (
          nodes.map((node) => (
            <ExplorerItem
              key={node.id}
              node={node}
              level={0}
              onToggle={handleToggle}
              onInsert={onInsert}
            />
          ))
        )}
      </ScrollArea>
    </Card>
  )
}
